import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import KkpColorBadge from '../../components/kkp/KkpColorBadge';
import secteursService from '../../services/secteursService';
import { formatCa } from '../../utils/secteursMapUtils';
import { ROUTES } from '../../config/routes';
import { AppAlert, AppSpinner } from '../../components/ui';

const SecteursEntrepriseDetailPage = () => {
  const { id } = useParams();
  const [entreprise, setEntreprise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await secteursService.getEntreprise(id);
        if (!cancelled) setEntreprise(data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Entreprise introuvable');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <MasterLayout>
        <AppSpinner />
      </MasterLayout>
    );
  }

  if (error || !entreprise) {
    return (
      <MasterLayout>
        <AppAlert>{error || 'Entreprise introuvable'}</AppAlert>
        <Link to={ROUTES.secteurs.entreprises} className="btn btn-outline-primary-600">
          Retour à la liste
        </Link>
      </MasterLayout>
    );
  }

  return (
    <MasterLayout>
      <Breadcrumb
        title={entreprise.nomRaisonSociale || 'Entreprise'}
        actions={(
          <Link to={ROUTES.secteurs.entreprises} className="btn btn-sm btn-outline-secondary">
            ← Retour à la liste
          </Link>
        )}
      />

      <div className="row g-3">
        <div className="col-lg-8">
          <div className="card">
            <div className="card-body">
              <h5 className="fw-semibold mb-1">{entreprise.nomRaisonSociale}</h5>
              {entreprise.sigle && <p className="text-secondary-light mb-3">{entreprise.sigle}</p>}
              <dl className="row mb-0">
                <dt className="col-sm-4">SIREN / SIRET</dt>
                <dd className="col-sm-8">{entreprise.siren} / {entreprise.siret || '—'}</dd>
                <dt className="col-sm-4">Adresse</dt>
                <dd className="col-sm-8">
                  {entreprise.adresse}<br />
                  {entreprise.codePostal} {entreprise.libelleCommune}
                </dd>
                <dt className="col-sm-4">Activité</dt>
                <dd className="col-sm-8">
                  {entreprise.activitePrincipale}
                  {entreprise.sectionActiviteLabel && (
                    <span className="text-secondary-light"> — {entreprise.sectionActiviteLabel}</span>
                  )}
                </dd>
                <dt className="col-sm-4">Effectif</dt>
                <dd className="col-sm-8">{entreprise.trancheEffectifLabel || entreprise.trancheEffectifSalarie || '—'}</dd>
                <dt className="col-sm-4">Nature juridique</dt>
                <dd className="col-sm-8">{entreprise.natureJuridiqueLabel || entreprise.natureJuridique || '—'}</dd>
                <dt className="col-sm-4">CA</dt>
                <dd className="col-sm-8">{formatCa(entreprise.chiffreAffaires)}</dd>
              </dl>
            </div>
          </div>
        </div>
        <div className="col-lg-4">
          <div className="card">
            <div className="card-body">
              <h6 className="fw-semibold mb-3">Secteur commercial</h6>
              {entreprise.secteur ? (
                <>
                  <KkpColorBadge color={entreprise.secteur.couleurSecteur} vivid className="badge mb-2">
                    {entreprise.secteur.nomCommercial || entreprise.secteur.nomSecteur}
                  </KkpColorBadge>
                  {entreprise.zone && (
                    <p className="mb-0 text-sm text-secondary-light">
                      Zone : {entreprise.zone.nom}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-secondary-light mb-0">Non attribué</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </MasterLayout>
  );
};

export default SecteursEntrepriseDetailPage;
